'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash } from '@phosphor-icons/react';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns, type InputColumn } from '../../../hooks/useNodeInputs';
import { ConditionBuilder, type Condition } from '../ConditionBuilder';
import type { DataType } from '../../../types';

interface ConditionalCase {
  id: string;
  conditions: Condition[];
  value: string;
  valueType: 'string' | 'number' | 'boolean';
}

interface ConditionalColumnConfigProps {
  nodeId: string;
  config: {
    newColumnName?: string;
    dataType?: DataType;
    cases?: ConditionalCase[];
    defaultValue?: string;
  };
}

const INFERRED_SOURCE_ID = '__inferred_from_params__';

function valueTypeToDataType(valueType: string | undefined): DataType {
  switch (valueType) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'time':
      return 'datetime';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

/** 当上游尚未注册 schema（如刚应用 AI 工作流）时，仍把条件里已引用的列名加入下拉选项 */
function mergeInputColumnsWithConditions(
  inputColumns: InputColumn[] | undefined,
  cases: ConditionalCase[] | undefined,
  inferredGroupName: string
): InputColumn[] | undefined {
  const base = inputColumns ?? [];
  const seen = new Set(base.map((c) => c.column.name));
  const extras: InputColumn[] = [];
  
  for (const c of cases ?? []) {
    for (const cond of c.conditions ?? []) {
      const name = cond.column?.trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      extras.push({
        column: {
          name,
          type: valueTypeToDataType(cond.valueType),
          nullable: true,
          index: base.length + extras.length,
        },
        source: {
          nodeId: INFERRED_SOURCE_ID,
          nodeType: 'inferred',
          nodeName: inferredGroupName,
        },
      });
    }
  }
  
  if (base.length === 0 && extras.length === 0) return undefined;
  if (extras.length === 0) return base.length > 0 ? base : undefined;
  return [...base, ...extras];
}

function generateId(): string {
  return `case_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function ConditionalColumnConfig({ nodeId, config }: ConditionalColumnConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const inputColumns = useNodeInputColumns(nodeId);

  const [newColumnName, setNewColumnName] = useState(config.newColumnName || '');
  const [dataType, setDataType] = useState<DataType>(config.dataType || 'string');
  const [defaultValue, setDefaultValue] = useState(config.defaultValue || '');
  const [cases, setCases] = useState<ConditionalCase[]>(
    config.cases?.length
      ? config.cases
      : [
          {
            id: generateId(),
            conditions: [{ column: '', operator: 'eq', value: '', valueType: 'string', logic: 'AND' }],
            value: '',
            valueType: 'string',
          },
        ]
  );

  // 当 config 变化时同步更新内部状态
  useEffect(() => {
    if (config.newColumnName !== undefined) setNewColumnName(config.newColumnName);
    if (config.dataType !== undefined) setDataType(config.dataType);
    if (config.defaultValue !== undefined) setDefaultValue(config.defaultValue);
    if (config.cases !== undefined && config.cases.length > 0) {
      setCases(config.cases);
    }
  }, [config.newColumnName, config.dataType, config.defaultValue, config.cases]);

  const columnOptions = useMemo(
    () =>
      mergeInputColumnsWithConditions(
        inputColumns,
        cases,
        t('nodes.config.conditional_column.inferredFromSaved')
      ),
    [inputColumns, cases, t]
  );

  const updateConfig = useCallback(
    (updates: Partial<ConditionalColumnConfigProps['config']>) => {
      const newConfig = {
        type: 'conditional_column',
        params: {
          newColumnName,
          dataType,
          cases,
          defaultValue,
          ...updates,
        },
      };
      updateNodeConfig(nodeId, newConfig);
    },
    [nodeId, updateNodeConfig, newColumnName, dataType, cases, defaultValue]
  );

  const handleColumnNameChange = (value: string) => {
    setNewColumnName(value);
    updateConfig({ newColumnName: value });
  };

  const handleDataTypeChange = (type: DataType) => {
    setDataType(type);
    updateConfig({ dataType: type });
  };

  const handleDefaultValueChange = (value: string) => {
    setDefaultValue(value);
    updateConfig({ defaultValue: value });
  };

  const addCase = () => {
    const newCase: ConditionalCase = {
      id: generateId(),
      conditions: [{ column: '', operator: 'eq', value: '', valueType: 'string', logic: 'AND' }],
      value: '',
      valueType: dataType === 'number' ? 'number' : dataType === 'boolean' ? 'boolean' : 'string',
    };
    const newCases = [...cases, newCase];
    setCases(newCases);
    updateConfig({ cases: newCases });
  };

  const removeCase = (index: number) => {
    if (cases.length <= 1) return;
    const newCases = cases.filter((_, i) => i !== index);
    setCases(newCases);
    updateConfig({ cases: newCases });
  };

  const updateCaseConditions = (caseIndex: number, newConditions: Condition[]) => {
    const newCases = cases.map((c, i) =>
      i === caseIndex ? { ...c, conditions: newConditions } : c
    );
    setCases(newCases);
    updateConfig({ cases: newCases });
  };

  const updateCaseValue = (caseIndex: number, value: string) => {
    const newCases = cases.map((c, i) => (i === caseIndex ? { ...c, value } : c));
    setCases(newCases);
    updateConfig({ cases: newCases });
  };

  const prefix = 'nodes.config.conditional_column';

  return (
    <div className="space-y-6">
      {/* 新列名称 */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-zinc-700">
          {t(`${prefix}.newColumnName`)}
        </label>
        <input
          type="text"
          value={newColumnName}
          onChange={(e) => handleColumnNameChange(e.target.value)}
          placeholder={t(`${prefix}.newColumnNamePlaceholder`)}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
        />
      </div>

      {/* 数据类型 */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-zinc-700">
          {t(`${prefix}.dataType`)}
        </label>
        <select
          value={dataType}
          onChange={(e) => handleDataTypeChange(e.target.value as DataType)}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="string">{t(`${prefix}.typeString`)}</option>
          <option value="number">{t(`${prefix}.typeNumber`)}</option>
          <option value="boolean">{t(`${prefix}.typeBoolean`)}</option>
          <option value="date">{t(`${prefix}.typeDate`)}</option>
          <option value="datetime">{t(`${prefix}.typeDatetime`)}</option>
        </select>
      </div>

      {/* 条件-值对（Switch/Case） */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-zinc-700">
            {t(`${prefix}.cases`)}
          </label>
          <span className="text-xs text-zinc-400">{t(`${prefix}.caseHint`)}</span>
        </div>

        <AnimatePresence>
          {cases.map((caseItem, caseIndex) => (
            <motion.div
              key={caseItem.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="p-4 bg-zinc-50 rounded-xl border border-zinc-200 space-y-4"
            >
              {/* Case 标题栏 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-zinc-500 bg-zinc-200 px-2 py-0.5 rounded">
                    {t(`${prefix}.case`)} {caseIndex + 1}
                  </span>
                  {caseIndex === 0 && (
                    <span className="text-[10px] text-zinc-400">
                      {t(`${prefix}.firstCasePriority`)}
                    </span>
                  )}
                </div>
                {cases.length > 1 && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => removeCase(caseIndex)}
                    className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                  >
                    <Trash className="w-3.5 h-3.5" />
                    {t(`${prefix}.deleteCase`)}
                  </motion.button>
                )}
              </div>

              {/* 条件构建器 */}
              <ConditionBuilder
                conditions={caseItem.conditions}
                onChange={(newConditions) => updateCaseConditions(caseIndex, newConditions)}
                columns={columnOptions}
                showLogic={true}
                showTimeGranularity={true}
                title={t(`${prefix}.conditions`)}
              />

              {/* 该条件下的返回值 */}
              <div className="space-y-2 pt-2 border-t border-zinc-200">
                <label className="text-xs font-medium text-zinc-500">
                  {t(`${prefix}.returnValue`)}
                </label>
                {dataType === 'boolean' ? (
                  <select
                    value={caseItem.value || 'true'}
                    onChange={(e) => updateCaseValue(caseIndex, e.target.value)}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="true">{t(`${prefix}.boolTrue`)}</option>
                    <option value="false">{t(`${prefix}.boolFalse`)}</option>
                  </select>
                ) : dataType === 'number' ? (
                  <input
                    type="number"
                    value={caseItem.value}
                    onChange={(e) => updateCaseValue(caseIndex, e.target.value)}
                    placeholder={t(`${prefix}.numberPlaceholder`)}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                  />
                ) : (
                  <input
                    type="text"
                    value={caseItem.value}
                    onChange={(e) => updateCaseValue(caseIndex, e.target.value)}
                    placeholder={t(`${prefix}.valuePlaceholder`)}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                  />
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* 添加新 Case 按钮 */}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={addCase}
          className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t(`${prefix}.addCase`)}
        </motion.button>
      </div>

      {/* 默认值 */}
      <div className="space-y-2 pt-4 border-t border-zinc-200">
        <label className="text-sm font-medium text-zinc-700">
          {t(`${prefix}.defaultValue`)}
        </label>
        <p className="text-xs text-zinc-400">{t(`${prefix}.defaultValueHint`)}</p>
        {dataType === 'boolean' ? (
          <select
            value={defaultValue || 'false'}
            onChange={(e) => handleDefaultValueChange(e.target.value)}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          >
            <option value="true">{t(`${prefix}.boolTrue`)}</option>
            <option value="false">{t(`${prefix}.boolFalse`)}</option>
          </select>
        ) : dataType === 'number' ? (
          <input
            type="number"
            value={defaultValue}
            onChange={(e) => handleDefaultValueChange(e.target.value)}
            placeholder={t(`${prefix}.defaultNumberPlaceholder`)}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
          />
        ) : (
          <input
            type="text"
            value={defaultValue}
            onChange={(e) => handleDefaultValueChange(e.target.value)}
            placeholder={t(`${prefix}.defaultValuePlaceholder`)}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
          />
        )}
      </div>
    </div>
  );
}
